import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Filter,
  X,
  Network,
} from "lucide-react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { toast } from "sonner";
import { useUser } from "../../../../context/UserContext";
import { useWorkflow } from "../../../../context/WorkflowContext";
import { Workflow } from "../../../../types";

interface WorkflowListViewProps {
  onCreateClick: () => void;
  onEditClick: (workflow: Workflow) => void;
}

const WorkflowListView: React.FC<WorkflowListViewProps> = ({
  onCreateClick,
  onEditClick,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tempFilter, setTempFilter] = useState<string>("my_workflows");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<Workflow | null>(null);

  const {
    workflows: workflowList,
    fetchWorkflows,
    loading,
    deleteWorkflowById,
    currentFilter,
    updateCurrentFilter,
  } = useWorkflow();

  const { userId, userDetails } = useUser();
  const isAdmin = userDetails?.role !== 'user';

  useEffect(() => {
    if (userId) {
      fetchWorkflows();
    }
  }, [userId, currentFilter, fetchWorkflows]);

  const filteredWorkflows = React.useMemo(() => {
    if (!searchQuery.trim()) return workflowList;

    const query = searchQuery.toLowerCase();
    return workflowList.filter((workflow) => {
      const name = workflow.workflow_name?.toLowerCase() || '';
      const description = workflow.description?.toLowerCase() || '';
      return name.includes(query) || description.includes(query);
    });
  }, [workflowList, searchQuery]);

  const handleFilterClick = () => {
    setTempFilter(currentFilter);
    setShowFilterModal(true);
  };

  const handleApplyFilter = () => {
    if (tempFilter !== currentFilter) {
      updateCurrentFilter(tempFilter);
      setSearchQuery("");
    }
    setShowFilterModal(false);
  };

  const handleClearFilters = () => {
    const defaultFilter = "my_workflows";
    if (currentFilter !== defaultFilter) {
      setTempFilter(defaultFilter);
      updateCurrentFilter(defaultFilter);
      setSearchQuery("");
    }
    setShowFilterModal(false);
  };

  const handleDeleteClick = (workflow: Workflow) => {
    setWorkflowToDelete(workflow);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!workflowToDelete?.id) return;

    setIsDeleting(true);
    try {
      await deleteWorkflowById(workflowToDelete.id);
      setShowDeleteModal(false);
      setWorkflowToDelete(null);
    } catch (error) {
      console.error("Error deleting workflow:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getFilterDisplayText = () => {
    switch (currentFilter) {
      case 'my_workflows':
        return 'My Workflows';
      case 'public_workflows':
        return 'Public Workflows';
      case 'all':
        return 'All Workflows';
      default:
        return 'My Workflows';
    }
  };

  const hasActiveFilters = () => {
    return currentFilter !== 'my_workflows' || searchQuery.trim() !== '';
  };

  const getChainLength = (workflow: Workflow) => {
    return workflow.assistant_chain?.length || 0;
  };

  return (
    <>
      <div className="mx-auto w-full max-w-screen-xl p-4">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-4">
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 bg-white text-gray-900 border border-gray-200 rounded-md text-xs sm:text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Search Workflows"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <button
                onClick={onCreateClick}
                className="bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium flex items-center ml-4 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Workflow
              </button>
            </div>

            {/* Filters */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleFilterClick}
                  className="flex items-center justify-between px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 min-w-[160px]"
                >
                  <span>{getFilterDisplayText()}</span>
                  <Filter size={14} className="ml-2 text-gray-400" />
                </button>
              </div>

              {hasActiveFilters() && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    updateCurrentFilter("my_workflows");
                  }}
                  className="flex items-center px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md border border-gray-200"
                >
                  <X size={14} className="mr-1" />
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Workflow List */}
          <div className="divide-y divide-gray-100">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
                <p className="mt-2 text-xs sm:text-sm text-gray-500">Loading workflows...</p>
              </div>
            ) : filteredWorkflows.length === 0 ? (
              <div className="p-8 text-center">
                <Network className="mx-auto h-12 w-12 text-gray-300" />
                <p className="mt-2 text-xs sm:text-sm text-gray-500">
                  {searchQuery ? "No workflows found matching your search" : "No workflows yet"}
                </p>
                {!searchQuery && (
                  <button
                    onClick={onCreateClick}
                    className="mt-4 text-xs sm:text-sm text-violet-600 hover:text-violet-700 font-medium"
                  >
                    Create your first workflow
                  </button>
                )}
              </div>
            ) : (
              filteredWorkflows.map((workflow) => (
                <div
                  key={workflow.id}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {workflow.workflow_name}
                        </h3>
                        {workflow.is_public && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Public
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                        {workflow.description}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Network size={12} />
                          {getChainLength(workflow)}-assistant chain
                        </span>
                        {workflow.updated_at && (
                          <span>
                            Updated {new Date(workflow.updated_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => onEditClick(workflow)}
                        className="p-2 text-muted hover:text-primary hover:bg-surface-2 rounded-md transition-colors"
                        title="Edit workflow"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(workflow)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete workflow"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      <Transition appear show={showFilterModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowFilterModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    Filter Workflows
                  </Dialog.Title>

                  <div className="space-y-2">
                    {[
                      { value: 'my_workflows', label: 'My Workflows' },
                      { value: 'public_workflows', label: 'Public Workflows' },
                      ...(isAdmin ? [{ value: 'all', label: 'All Workflows' }] : []),
                    ].map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="radio"
                          name="filter"
                          value={option.value}
                          checked={tempFilter === option.value}
                          onChange={(e) => setTempFilter(e.target.value)}
                          className="h-4 w-4 text-violet-600 focus:ring-purple-500"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      onClick={handleClearFilters}
                      className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      onClick={handleApplyFilter}
                      className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-md transition-colors"
                    >
                      Apply
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Delete Confirmation Modal */}
      <Transition appear show={showDeleteModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowDeleteModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Delete Workflow
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete "{workflowToDelete?.workflow_name}"? This action cannot be undone.
                    </p>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      disabled={isDeleting}
                      className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDelete}
                      disabled={isDeleting}
                      className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 flex items-center transition-colors"
                    >
                      {isDeleting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Deleting...
                        </>
                      ) : (
                        'Delete'
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default WorkflowListView;
